import { useAuthStore } from '@/features/auth/store'
import type { SigninResponse, UserInfoResponse } from '@/features/auth/types'
import type { CreateUserAddressDto, UserAddressResponseDto } from '@/features/auth/types/address'
import type {
  CreateUserBankAccountDto,
  UserBankAccountResponseDto,
} from '@/features/auth/types/bankAccount'
import { api } from '@/apis/base'
import { queryClient } from '@/lib/queryClient'
import { authKeys } from '../hooks/queryKeys'
import { AxiosError } from 'axios'

// Re-export SigninResponse from types
export type { SigninResponse } from '@/features/auth/types'

interface SignupData {
  email: string
  password: string
  confirmPassword: string
  name: string
  firstNameKana: string
  lastNameKana: string
}

interface SigninData {
  email: string
  password: string
}

interface AuthApi {
  signin(data: SigninData): Promise<SigninResponse['user']>
  signup(data: SignupData): Promise<Omit<SigninResponse['user'], 'id'>>
  logout(): Promise<void>
  isAuthenticated(): Promise<{ authenticated: boolean; user: SigninResponse['user'] | null }>
  getCurrentUser(): Promise<UserInfoResponse>
  deleteAccount(): Promise<void>
  checkEmailAvailability(
    email: string
  ): Promise<{ available: boolean; code?: number; remainingDays?: number }>
  updateUser(data: Partial<SigninResponse['user']>): Promise<SigninResponse['user']>
  updatePassword(
    currentPassword: string,
    newPassword: string
  ): Promise<{ success: boolean; message: string }>
  // 비밀번호 재설정 관련 메서드
  requestPasswordReset(email: string): Promise<{
    success: boolean
    message: string
    expiresIn: number
    code: string
  }>
  validateResetToken(token: string): Promise<{ valid: boolean; email?: string; error?: string }>
  completePasswordReset(
    token: string,
    newPassword: string
  ): Promise<{ success: boolean; message: string }>
  // 배송정보 관련 메서드
  getAddress(): Promise<UserAddressResponseDto | null>
  createOrUpdateAddress(data: CreateUserAddressDto): Promise<UserAddressResponseDto>
  deleteAddress(): Promise<void>
  // 계좌정보 관련 메서드
  getBankAccount(): Promise<UserBankAccountResponseDto | null>
  createOrUpdateBankAccount(data: CreateUserBankAccountDto): Promise<UserBankAccountResponseDto>
  deleteBankAccount(): Promise<void>
}

export const authApi: AuthApi = {
  async signin(data: SigninData): Promise<SigninResponse['user']> {
    try {
      const response = await api.post<SigninResponse>('/auth/signin', data, {
        _skipAuth: true,
      } as Parameters<typeof api.post>[2])

      const { user, accessToken, refreshToken } = response.data

      // Update auth store
      const authStore = useAuthStore.getState()
      authStore.setAuth(user, accessToken, refreshToken)
      queryClient.invalidateQueries({ queryKey: authKeys.user() })

      return user
    } catch (error) {
      console.error('signin failed:', error) // Debug log
      throw error
    }
  },

  async signup(data: SignupData): Promise<Omit<SigninResponse['user'], 'id'>> {
    try {
      const response = await api.post('/auth/signup', data)

      return response.data
    } catch (error) {
      console.error('signup failed:', error) // Debug log
      throw error
    }
  },

  async logout(): Promise<void> {
    try {
      // Send logout request to server
      await api.post('/auth/logout', {})

      // Clear auth store
      const authStore = useAuthStore.getState()
      authStore.clearAuth()
      queryClient.removeQueries({ queryKey: authKeys.user(), exact: true })

      // No need for alert here since navigation will happen immediately
    } catch (error) {
      console.error('Logout failed:', error)
      // Still clear local data even if server request fails
      const authStore = useAuthStore.getState()
      authStore.clearAuth()
      queryClient.removeQueries({ queryKey: authKeys.user(), exact: true })
      throw error
    }
  },

  async isAuthenticated(): Promise<{
    authenticated: boolean
    user: SigninResponse['user'] | null
  }> {
    try {
      const response = await api.get<SigninResponse['user']>('/auth/me')

      if (!response.data || !response.data.id) {
        throw new Error('Invalid user data')
      }

      // Map backend response to frontend user format
      const authStore = useAuthStore.getState()
      const updatedUser = {
        id: response.data.id,
        email: response.data.email,
        name: response.data.name,
        firstNameKana: response.data.firstNameKana,
        lastNameKana: response.data.lastNameKana,
        lineId: response.data.lineInfo?.lineUserId || undefined,
        // Other fields will be undefined as they're not in backend response
      }

      authStore.setUser(updatedUser as SigninResponse['user'])
      authStore.setLoading(false)

      return {
        authenticated: authStore.checkAuthStatus(),
        user: updatedUser,
      }
    } catch {
      // Clear auth store if authentication fails
      const authStore = useAuthStore.getState()
      authStore.clearAuth()

      return {
        authenticated: false,
        user: null,
      }
    }
  },

  async getCurrentUser(): Promise<UserInfoResponse> {
    try {
      const response = await api.get<UserInfoResponse>('/auth/me')

      if (!response.data || !response.data.id) {
        throw new Error('Invalid user data received')
      }

      // Map backend response to frontend user format
      const mappedUser = {
        id: response.data.id,
        email: response.data.email,
        name: response.data.name,
        firstNameKana: response.data.firstNameKana,
        lastNameKana: response.data.lastNameKana,
        lineId: response.data.lineInfo?.lineUserId || undefined,
        // Add other fields as needed
      }

      const authStore = useAuthStore.getState()
      authStore.setUser(mappedUser as SigninResponse['user'])
      queryClient.setQueryData(authKeys.user(), response.data)

      return response.data
    } catch (error) {
      console.error('getCurrentUser failed:', error)
      // Clear auth store
      const authStore = useAuthStore.getState()
      authStore.clearAuth()
      throw error
    }
  },

  async deleteAccount(): Promise<void> {
    try {
      await api.delete('/auth/user')
      const authStore = useAuthStore.getState()
      authStore.clearAuth()
      queryClient.removeQueries({ queryKey: authKeys.user(), exact: true })
    } catch (error) {
      console.error('Account deletion failed:', error)
      throw error
    }
  },

  async checkEmailAvailability(email: string): Promise<{ available: boolean }> {
    try {
      const response = await api.get<{ available: boolean }>(
        `/auth/check-email?email=${encodeURIComponent(email)}`
      )
      return response.data
    } catch (error) {
      console.error('Email check failed:', error)
      throw error
    }
  },

  async updateUser(data: Partial<SigninResponse['user']>): Promise<SigninResponse['user']> {
    try {
      const response = await api.patch<SigninResponse['user']>('/auth/user', data)
      return response.data
    } catch (error) {
      console.error('Update user failed:', error)
      throw error
    }
  },

  async updatePassword(
    currentPassword: string,
    newPassword: string
  ): Promise<{ success: boolean; message: string }> {
    try {
      const response = await api.patch<{ success: boolean; message: string }>('/auth/password', {
        currentPassword,
        newPassword,
      })
      return response.data
    } catch (error) {
      if (error && typeof error === 'object' && 'response' in error) {
        const axiosError = error as { response?: { status?: number } }
        if (axiosError.response?.status === 401) {
          throw new Error('現在のパスワードが正しくありません。')
        }
      }
      throw error
    }
  },

  // 배송정보 관련 API
  async getAddress(): Promise<UserAddressResponseDto | null> {
    try {
      const response = await api.get<UserAddressResponseDto>('/auth/address')
      return response.data
    } catch (error: unknown) {
      if (error instanceof AxiosError && error.response?.status === 404) {
        return null
      }
      console.error('getAddress failed:', error)
      throw error
    }
  },

  async createOrUpdateAddress(data: CreateUserAddressDto): Promise<UserAddressResponseDto> {
    try {
      const response = await api.post<UserAddressResponseDto>('/auth/address', data)
      return response.data
    } catch (error) {
      console.error('createOrUpdateAddress failed:', error)
      throw error
    }
  },

  async deleteAddress(): Promise<void> {
    try {
      await api.delete('/auth/address')
    } catch (error) {
      console.error('deleteAddress failed:', error)
      throw error
    }
  },

  // 계좌정보 관련 API
  async getBankAccount(): Promise<UserBankAccountResponseDto | null> {
    try {
      const response = await api.get<UserBankAccountResponseDto>('/auth/bank-account')
      return response.data
    } catch (error: unknown) {
      if (error instanceof AxiosError && error.response?.status === 404) {
        return null
      }
      console.error('getBankAccount failed:', error)
      throw error
    }
  },

  async createOrUpdateBankAccount(
    data: CreateUserBankAccountDto
  ): Promise<UserBankAccountResponseDto> {
    try {
      const response = await api.post<UserBankAccountResponseDto>('/auth/bank-account', data)
      return response.data
    } catch (error) {
      console.error('createOrUpdateBankAccount failed:', error)
      throw error
    }
  },

  async deleteBankAccount(): Promise<void> {
    try {
      await api.delete('/auth/bank-account')
    } catch (error) {
      console.error('deleteBankAccount failed:', error)
      throw error
    }
  },

  // 비밀번호 재설정 API
  async requestPasswordReset(email: string): Promise<{
    success: boolean
    message: string
    expiresIn: number
    code: string
  }> {
    try {
      const response = await api.post<{
        success: boolean
        message: string
        expiresIn: number
        code: string
      }>('/auth/password-reset/request', { email }, {
        _skipAuth: true,
      } as Parameters<typeof api.post>[2])
      return response.data
    } catch (error) {
      console.error('requestPasswordReset failed:', error)
      throw error
    }
  },

  async validateResetToken(token: string): Promise<{
    valid: boolean
    email?: string
    error?: string
  }> {
    try {
      const response = await api.get<{ valid: boolean; email?: string; error?: string }>(
        `/auth/password-reset/validate?token=${encodeURIComponent(token)}`,
        {
          _skipAuth: true,
        } as Parameters<typeof api.get>[1]
      )
      return response.data
    } catch (error) {
      console.error('validateResetToken failed:', error)
      throw error
    }
  },

  async completePasswordReset(
    token: string,
    newPassword: string
  ): Promise<{ success: boolean; message: string }> {
    try {
      const response = await api.post<{ success: boolean; message: string }>(
        '/auth/password-reset/complete',
        { token, newPassword },
        {
          _skipAuth: true,
        } as Parameters<typeof api.post>[2]
      )
      return response.data
    } catch (error) {
      console.error('completePasswordReset failed:', error)
      throw error
    }
  },
}
